import { DAY, HOUR, MINUTE, deriveHistory } from '../domain/derive.ts';
import type { Asset, AssetHistory, Coordinates, Device, Gateway, LocationFix, Place, Timestamp } from '../domain/types.ts';
import { center } from './catalog.ts';
import { I10_OBSERVATIONS } from './routes.ts';
import { seededRandom } from './random.ts';
export interface Waypoint extends Coordinates { at: Timestamp; placeId?: string; checkpoint?: number }
export interface Journey {
  id: string; from: Timestamp; to: Timestamp; originId: string; destinationId: string;
  points: readonly Waypoint[]; freight: boolean; delayed?: boolean;
}
export interface HistoryPlan {
  asset: Asset; device: Device; initialPlaceId: string; journeys: Journey[]; cutoff: Timestamp;
}
export interface HistoryContext { now: Timestamp; from: Timestamp; places: readonly Place[]; gateways: readonly Gateway[]; seed: string }
/** Fixed Arizona route clock makes the five story times stable across host timezone/DST. */
export const phoenixMidnight = (now: number) => Math.floor((now - 7 * HOUR) / DAY) * DAY + 7 * HOUR;
function journey(id: string, from: number, duration: number, a: Place, b: Place, via: Coordinates[], freight = false): Journey {
  const points = [center(a), ...via, center(b)].map((p, i, all) => ({ ...p, at: from + duration * i / (all.length - 1), ...(i === 0 ? { placeId: a.id } : i === all.length - 1 ? { placeId: b.id } : {}) }));
  return { id, from, to: from + duration, originId: a.id, destinationId: b.id, points, freight };
}
function i10(id: string, start: number, places: readonly Place[], reverse = false): Journey {
  const points = I10_OBSERVATIONS.map(p => ({ lat: p.lat, lng: p.lng, at: start + (p.minute - 370) * MINUTE,
    ...('checkpoint' in p ? { placeId: places[p.checkpoint].id, checkpoint: p.checkpoint } : {}) }));
  if (reverse) {
    points.reverse();
    points.forEach((p, i) => { p.at = start + (825 - I10_OBSERVATIONS[I10_OBSERVATIONS.length - 1 - i].minute) * MINUTE; });
  }
  return { id, from: start, to: start + 455 * MINUTE, originId: places[reverse ? 4 : 0].id, destinationId: places[reverse ? 0 : 4].id, points, freight: true };
}
export function makePlan(asset: Asset, device: Device, index: number, ctx: HistoryContext): HistoryPlan {
  const p = ctx.places, w = asset.workspaceId, day = phoenixMidnight(ctx.now);
  const journeys: Journey[] = [];
  let initialPlaceId = p[0].id;
  const add = (j: Journey) => journeys.push(j);
  const named = (key: string) => p.find(place => place.id.endsWith(`:place:${key}`))!;
  if (w === 'personal') {
    // Daily life follows local daylight, with a distinct weekend and errands pattern.
    const localDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(ctx.now);
    const midnight = Date.parse(`${localDay}T00:00:00-08:00`);
    for (let d = 44; d >= 2; d--) {
      if (index === 2) continue; // Luggage stays home between trips.
      const start = midnight - d * DAY + (8 + index / 5) * HOUR;
      const weekend = [0, 6].includes(new Date(start).getUTCDay());
      const dest = weekend ? named((d + index) % 2 ? 'park' : 'weekend') : named(index === 4 ? 'park' : 'office');
      const via = [{ lat: 37.7519, lng: -122.4278 }, { lat: 37.765, lng: -122.4196 }, { lat: 37.7797, lng: -122.4139 }];
      add(journey(`${asset.id}:out:${d}`, start, (32 + (d * 7 + index) % 18) * MINUTE, p[0], dest, via));
      const errand = named((d + index) % 3 ? 'groceries' : 'gym');
      add(journey(`${asset.id}:errand:${d}`, start + (weekend ? 3 : 8) * HOUR, 28 * MINUTE, dest, errand, [{ lat: 37.765, lng: -122.4196 }]));
      add(journey(`${asset.id}:back:${d}`, start + (weekend ? 4.5 : 9.5) * HOUR, 18 * MINUTE, errand, p[0], []));
    }
    if (index === 0) add(journey(`${asset.id}:coffee`, ctx.now - 125 * MINUTE, 42 * MINUTE, p[0], named('cafe'),
      [{ lat: 37.7519, lng: -122.4278 }, { lat: 37.765, lng: -122.4196 }, { lat: 37.7731, lng: -122.4138 }]));
    if (index === 4) add(journey(`${asset.id}:park`, ctx.now - 195 * MINUTE, 19 * MINUTE, p[0], p[2], [{ lat: 37.7519, lng: -122.4278 }]));
    if (index === 2) {
      const start = ctx.now - 5 * DAY - 7 * HOUR;
      add(journey(`${asset.id}:airport`, start, HOUR, p[0], p[3], [{ lat: 37.6639, lng: -122.401 }]));
      add(journey(`${asset.id}:flight`, start + 2 * HOUR, 130 * MINUTE, p[3], p[4], []));
      add(journey(`${asset.id}:seattle`, start + 5 * HOUR, 40 * MINUTE, p[4], p[5], [{ lat: 47.505, lng: -122.297 }]));
      add(journey(`${asset.id}:airport-return`, ctx.now - 6 * HOUR, 40 * MINUTE, p[5], p[4], [{ lat: 47.505, lng: -122.297 }]));
      add(journey(`${asset.id}:flight-return`, ctx.now - 4 * HOUR, 130 * MINUTE, p[4], p[3], []));
      add(journey(`${asset.id}:home`, ctx.now - 35 * MINUTE, 55 * MINUTE, p[3], p[0],
        [{ lat: 37.635, lng: -122.407 }, { lat: 37.6639, lng: -122.401 }, { lat: 37.706, lng: -122.405 }]));
    }
  } else if (w === 'coastline') {
    if (index === 0) {
      for (let d = 43; d >= 4; d -= 6) {
        add(i10(`${asset.id}:lane:${d}`, day - d * DAY + 370 * MINUTE, p));
        add(i10(`${asset.id}:return:${d}`, day - (d - 2) * DAY + 390 * MINUTE, p, true));
      }
      add(i10('coastline:shipment:4417', day - DAY + 370 * MINUTE, p));
    } else {
      const origins = [p[0], named('portla'), named('hueneme'), named('oakland')];
      const destinations = [named('phoenix'), p[4], named('scottsdale'), named('tucson'), named('vegas'), named('albuquerque'), named('sandiego'), named('palmsprings')];
      const origin = origins[index % 4], dest = destinations[(index * 3) % 8];
      initialPlaceId = origin.id;
      const western = center(dest).lng < -116;
      const via = western ? [{ lat: 34.055, lng: -117.6 }, { lat: 33.94, lng: -117.28 }] : dest.id.endsWith('vegas')
        ? [{ lat: 34.055, lng: -117.6 }, { lat: 34.897, lng: -117.024 }, { lat: 35.61, lng: -115.39 }]
        : dest.id.endsWith('albuquerque') ? [{ lat: 34.897, lng: -117.024 }, { lat: 35.19, lng: -114.05 }, { lat: 35.19, lng: -111.65 }, { lat: 35.53, lng: -108.74 }]
        : I10_OBSERVATIONS.filter((_, i) => i % 3 === 0).slice(2, -1).map(({lat,lng}) => ({lat,lng}));
      if (origin.id.endsWith('oakland')) via.unshift({lat: 35.38, lng: -119.04}, {lat: 34.85, lng: -118.87});
      const duration = (western ? 4.2 : dest.id.endsWith('vegas') ? 6.5 : dest.id.endsWith('albuquerque') ? 13 : 8.3) * HOUR + (index % 4 === 3 ? 7 * HOUR : index % 4 * 40 * MINUTE);
      for (let d = 43; d >= 5; d -= 6) {
        const start = day - d * DAY + (300 + index * 11 % 150) * MINUTE;
        add(journey(`${asset.id}:lane:${d}`, start, duration + (d % 4) * 13 * MINUTE, origin, dest, via, true));
        add(journey(`${asset.id}:return:${d}`, start + 2 * DAY, duration, dest, origin, [...via].reverse(), true));
      }
      const active = index < 14;
      const start = active ? ctx.now - duration * (0.18 + index % 5 * 0.15) : index < 29
        ? ctx.now - duration - (7 + index * 2.7) * HOUR : ctx.now + (index - 28) * 2.3 * HOUR;
      const route = journey(`${asset.id}:${active ? 'active' : index < 29 ? 'recent' : 'planned'}`, start, duration, origin, dest, via, true);
      route.delayed = active && index % 4 === 0;
      add(route);
    }
  } else if (w === 'ridgeline') {
    initialPlaceId = index < 33 ? p[0].id : p[1 + index % 3].id;
    for (let d = 44; d >= 2; d--) {
      if ((d + index) % 7 > 4 || index >= 45) continue;
      const a = p.find(place => place.id === initialPlaceId)!;
      const b = p[1 + index % 3];
      if (a.id === b.id) continue;
      const start = day - d * DAY + (7 + index / 120) * HOUR;
      const via = b.id.endsWith('glendale') ? [{ lat: 33.4501, lng: -112.151 }, { lat: 33.509, lng: -112.151 }, { lat: 33.5387, lng: -112.151 }]
        : [{ lat: 33.4336, lng: -112.134 }, { lat: 33.461, lng: -112.134 }, { lat: 33.461, lng: -112.057 }, { lat: 33.430, lng: -112.019 }, { lat: 33.4152, lng: -111.94 }];
      add(journey(`${asset.id}:out:${d}`, start, 55 * MINUTE, a, b, via));
      add(journey(`${asset.id}:back:${d}`, start + 9 * HOUR, 55 * MINUTE, b, a, [...via].reverse()));
    }
    if (index > 0 && index < 18 && index % 3 !== 0) add(journey(`${asset.id}:crew-dispatch`, ctx.now - (2 + index / 4) * HOUR, (41 + index % 13) * MINUTE, p[0], p[1 + index % 3],
      [{lat: 33.461, lng: -112.134}, {lat: 33.461, lng: -112.057}]));
    if (index === 0) add(journey(`${asset.id}:yard-exit`, ctx.now - 85 * MINUTE, 60 * MINUTE, p[0], p[1],
      [{ lat: 33.4336, lng: -112.134 }, { lat: 33.461, lng: -112.134 }, { lat: 33.461, lng: -112.057 }, { lat: 33.430, lng: -112.019 }, { lat: 33.4152, lng: -111.94 }]));
  } else {
    const destinations = [named('dallas'), named('la'), named('elpaso'), named('tucson'), named('vegas')];
    const dest = destinations[index % 5];
    const via = index % 5 === 1
      ? I10_OBSERVATIONS.slice().reverse().filter((_, i) => i % 3 === 0).map(({ lat, lng }) => ({ lat, lng }))
      : index % 5 === 4 ? [{lat: 33.9, lng: -112.66}, {lat: 34.23, lng: -112.97}, {lat: 35.19, lng: -114.05}, {lat: 35.98, lng: -114.83}]
      : [{lat:32.879,lng:-111.757}, {lat:32.139,lng:-110.946}, ...(index % 5 === 3 ? [] : [{lat:32.267,lng:-109.832}, {lat:32.255,lng:-107.755}, {lat:32.286,lng:-106.766}, {lat:31.7735,lng:-106.3712}]),
        ...(index % 5 === 0 ? [{lat:31.045,lng:-104.839}, {lat:30.885,lng:-102.884}, {lat:31.998,lng:-102.079}, {lat:32.448,lng:-99.733}, {lat:32.746,lng:-97.329}] : [])];
    const duration = [23, 9, 8, 2.5, 6][index % 5] * HOUR + index % 7 * 9 * MINUTE;
    for (let d = 43; d >= 6; d -= 6) {
      const start = day - d * DAY + (330 + index * 7 % 130) * MINUTE;
      add(journey(`${asset.id}:lane:${d}`, start, duration, p[0], dest, via, true));
      add(journey(`${asset.id}:return:${d}`, start + 2 * DAY, duration, dest, p[0], [...via].reverse(), true));
    }
    if (index > 0 && index % 4 === 1) add(journey(`${asset.id}:active`, ctx.now - duration * (0.18 + index % 5 * 0.15), duration, p[0], dest, via, true));
    else if (index > 0 && index % 4 !== 0) add(journey(`${asset.id}:received`, ctx.now - duration - (8 + index * 1.3) * HOUR, duration, p[0], dest, via, true));

  }
  journeys.sort((a, b) => a.from - b.from);
  return { asset, device, initialPlaceId, journeys, cutoff: w === 'northstar' && index === 0 ? ctx.now - 6 * HOUR : ctx.now - (w === 'personal' ? [40, 3, 17, 8, 12][index] : 2 + index / 3 + seededRandom(`${ctx.seed}:age:${asset.id}`)() * 5) * MINUTE };
}
export function fixId(assetId: string, at: number): string { return `${assetId}:fix:${Math.round(at)}`; }
export function generateFixes(plan: HistoryPlan, ctx: HistoryContext, from = ctx.from): LocationFix[] {
  const fixes: LocationFix[] = [];
  const end = Math.floor(Math.min(ctx.now - 2 * MINUTE, plan.cutoff));
  const push = (at: number, p: Coordinates, placeId?: string) => {
    at = Math.round(at);
    if (at < from || at > end) return;
    const random = seededRandom(`${ctx.seed}:${plan.asset.id}:${at}`);
    const gateway = ctx.gateways.find(g => g.placeId === placeId);
    fixes.push({ id: fixId(plan.asset.id, at), workspaceId: plan.asset.workspaceId, assetId: plan.asset.id, deviceId: plan.device.id,
      seenAt: at, receivedAt: Math.min(ctx.now, at + 15_000 + Math.floor(random() * 90_000)),
      lat: p.lat + (random() - 0.5) * 0.00012, lng: p.lng + (random() - 0.5) * 0.00012,
      source: gateway ? 'gateway' : 'phone', gatewayId: gateway?.id ?? null, confidence: gateway ? 3 : 2, vendorPositionType: 'BEACON' });
  };
  const stay = (start: number, until: number, placeId: string) => {
    const p = ctx.places.find(place => place.id === placeId)!;
    const gateway = ctx.gateways.some(g => g.placeId === placeId);
    const step = (gateway ? 10 : 20) * MINUTE;
    const phase = Math.floor(seededRandom(`${ctx.seed}:phase:${plan.asset.id}`)() * step);
    for (let t = Math.ceil((Math.max(from, start) - phase) / step) * step + phase; t < Math.min(end + 1, until); t += step) {
      // Phones have ordinary encounter gaps; warehouse readers are steady.
      if (!gateway && seededRandom(`${ctx.seed}:gap:${plan.asset.id}:${t}`)() < 0.23) continue;
      push(t, center(p), placeId);
    }
  };
  let time = ctx.from, placeId = plan.initialPlaceId;
  for (const route of plan.journeys) {
    stay(time, route.from, placeId);
    if (route.from > end) { time = end + 1; break; }
    if (route.to >= from) for (const point of route.points) push(point.at, point, point.placeId);
    time = route.to; placeId = route.destinationId;
  }
  stay(time, end + 1, placeId);
  // A final encounter at the stationary place, never at an unobserved future destination.
  const active = plan.journeys.some(j => j.from <= end && j.to > end);
  if (!active) push(end, center(ctx.places.find(p => p.id === placeId)!), placeId);
  fixes.sort((a, b) => a.seenAt - b.seenAt);
  return fixes.filter((f, i) => !i || f.seenAt !== fixes[i - 1].seenAt);
}
export function generateHistory(plan: HistoryPlan, ctx: HistoryContext): AssetHistory {
  const fixes = generateFixes(plan, ctx);
  const satelliteCheckIns = plan.device.satelliteEnabled ? Array.from({ length: 15 }, (_, i) => {
    const seenAt = ctx.now - (44 - i * 3) * DAY - 4 * HOUR;
    return { id: `${plan.asset.id}:satellite:${i}`, workspaceId: plan.asset.workspaceId, assetId: plan.asset.id, deviceId: plan.device.id, seenAt, receivedAt: seenAt + 2 * HOUR };
  }).filter(c => c.seenAt >= ctx.from) : [];
  return { fixes, ...deriveHistory(fixes, ctx.places), satelliteCheckIns };
}
